// material-ui
import { Theme } from '@mui/material/styles';

// project imports
import getColors from 'utils/getColors';

// types
import { ExtendedStyleProps } from 'types/extended';

// ==============================|| CHIP - COLORS ||============================== //

function getColor({ color, theme }: ExtendedStyleProps) {
  const colors = getColors(theme, color);
  const { dark } = colors;

  return {
    '&.Mui-focusVisible': {
      outline: `2px solid ${dark}`,
      outlineOffset: 2
    }
  };
}

function getColorStyle({ color, theme }: ExtendedStyleProps) {
  const colors = getColors(theme, color);
  const { light, lighter, main, darker } = colors;

  return {
    color: main,
    backgroundColor: lighter,
    borderColor: colors[100 as keyof typeof colors],
    ...theme.applyStyles('dark', { color: darker }),
    '& .MuiChip-deleteIcon': {
      color: main,
      '&:hover': {
        color: light
      }
    }
  };
}

// ==============================|| OVERRIDES - CHIP ||============================== //

export default function Chip(theme: Theme) {
  const defaultLightChip = getColorStyle({ color: 'secondary', theme });
  return {
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          border: '1px solid',
          borderColor: theme.palette.divider,
          '&:active': {
            boxShadow: 'none'
          },
          '&.MuiChip-colorPrimary': getColor({ color: 'primary', theme }),
          '&.MuiChip-colorSecondary': {
            ...getColor({ color: 'secondary', theme }),
            '.MuiDataGrid-row:hover &': {
              backgroundColor: theme.palette.common.white
            }
          },
          '&.MuiChip-colorError': getColor({ color: 'error', theme }),
          '&.MuiChip-colorInfo': getColor({ color: 'info', theme }),
          '&.MuiChip-colorSuccess': getColor({ color: 'success', theme }),
          '&.MuiChip-colorWarning': getColor({ color: 'warning', theme }),
          '&.MuiChip-colorCustomBlack': {
            color: theme.palette.secondary.darker,
            '.MuiDataGrid-row:hover &': {
              backgroundColor: theme.palette.common.white
            }
          }
        },
        sizeLarge: {
          fontSize: '1rem',
          height: 40
        }
      },
      variants: [
        {
          props: { variant: 'light' },
          style: {
            ...defaultLightChip,
            '&.MuiChip-colorPrimary': getColorStyle({ color: 'primary', theme }),
            '&.MuiChip-colorSecondary': {
              ...getColorStyle({ color: 'secondary', theme }),
              '.MuiDataGrid-row:hover &': {
                backgroundColor: theme.palette.common.white
              }
            },
            '&.MuiChip-colorError': getColorStyle({ color: 'error', theme }),
            '&.MuiChip-colorInfo': getColorStyle({ color: 'info', theme }),
            '&.MuiChip-colorSuccess': getColorStyle({ color: 'success', theme }),
            '&.MuiChip-colorWarning': getColorStyle({ color: 'warning', theme }),
            '&.MuiChip-colorCustomBlack': {
              '.MuiDataGrid-row:hover &': {
                backgroundColor: theme.palette.common.white
              }
            }
          }
        },
        {
          props: { variant: 'combined' },
          style: {
            ...defaultLightChip,
            '&.MuiChip-colorPrimary': getColorStyle({ color: 'primary', theme }),
            '&.MuiChip-colorSecondary': {
              ...getColorStyle({ color: 'secondary', theme }),
              '.MuiDataGrid-row:hover &': {
                backgroundColor: theme.palette.common.white
              }
            },
            '&.MuiChip-colorError': getColorStyle({ color: 'error', theme }),
            '&.MuiChip-colorInfo': getColorStyle({ color: 'info', theme }),
            '&.MuiChip-colorSuccess': getColorStyle({ color: 'success', theme }),
            '&.MuiChip-colorWarning': getColorStyle({ color: 'warning', theme }),
            '&.MuiChip-colorCustomBlack': {
              '.MuiDataGrid-row:hover &': {
                backgroundColor: theme.palette.common.white
              }
            }
          }
        }
      ]
    }
  };
}
